<template lang="pug">
  WtTile(ancestor)#setup-page.is-flex.is-flex-direction-column.is-align-items-center
    WtTile(parent :col="12").main-card
      WtTile(child :col="12")
        // SUCCESS
        section.section.card-wrapper(v-if="success")
          WtSvgIcon(icon="be-well-logo" width="200px" height="200px")
          .columns.is-centered
            .column.is-half-desktop
              .is-size-1.has-text-centered.has-text-weight-bold
                | Success!
              br
              .is-size-1.has-text-centered.has-text-weight-bold
                | We're logging you into your program now, one moment ...
        // CREATE PROGRAM
        section.section.card-wrapper(v-else)
          .create-program-header.mb-5
            .level
              .level-item
                WtSvgIcon(icon="be-well-logo" width="200px" height="200px")
                WtTitle.pb-5(h1) Get Started With Be Well!
          .create-program-columns.columns
            .column.is-one-third
              .product-chosen.create-program-column.p-3
                ProductCard(
                  :product="activeProductObject"
                )
            .column
              .setup-managed.create-program-column.p-3
                WtTitle(h3) Create Your Account
                ProgramAdminDetails(
                  @newProgramAdminDetails="newProgramAdminDetails"
                )
                ProgramDetails.mt-4(
                  @newProgramDetails="newProgramDetails"
                )
                TermsAndConditions.mt-6(@acceptTerms="setTermsAccepted")
                .footer-button.pt-6.px-4
                  .has-text-danger(v-if="showErrorMessage") {{ paymentErrorMessage }}
                  WtButton(
                    @click.native="submitProgramSetup"
                    color="primary"
                    fullwidth
                    :disabled="disableSubmitProgram"
                    :loading="processing"
                  ) Submit

</template>

<script>
import { mapActions } from "vuex";
import WtTitle from "@@/WtTitle/WtTitle";
import UniquenessCheck from "@@/WtUniquenessCheckInput/WtUniquenessCheckInput";
import WtButton from "@@/WtButton/WtButton";
import WtLabel from "@@/WtLabel/WtLabel";
import WtTile from "@@/WtTile/WtTile";
import ProductCard from "@@/ProgramSettingsAdmin/Subscriptions/ProductCard";
import TermsAndConditions from "@@/CreateProgram/TermsAndConditions";
import ProgramAdminDetails from "@@/CreateProgram/ProgramAdminDetails";
import ProgramDetails from "@@/CreateProgram/ProgramDetails";
import WtSvgIcon from "@@/WtSvgIcon/WtSvgIcon";

export default {
  name: "SetupPage",

  components: {
    ProgramAdminDetails,
    ProgramDetails,
    ProductCard,
    WtTile,
    WtLabel,
    WtButton,
    WtSvgIcon,
    UniquenessCheck,
    WtTitle,
    TermsAndConditions
  },

  data() {
    return {
      success: false,
      createProgramDetailsStep: 0,
      programAdminDetailsValid: false,
      programAdminDetailsData: {},
      programDetailsValid: false,
      programDetailsData: {},
      processing: false,
      submitDisabled: true,
      products: [
        {
          name: "BeWellCore"
        },
        {
          name: "BeWellPlus"
        },
        {
          name: "BeWellPro"
        }
      ],
      activeProductName: "bewellcore",
      termsAccepted: false,
      programStoreError: false,
      submitError: false,
      programCreated: false
    };
  },

  computed: {
    showErrorMessage() {
      return this.programStoreError || this.submitError;
    },

    activeProductObject() {
      return (
        this.products.find(
          product =>
            this._.toLower(product.name) ===
            this._.toLower(this.activeProductName)
        ) || {}
      );
    },

    createProgramPayload() {
      return { ...this.programAdminDetailsData, ...this.programDetailsData };
    },

    createProgramPayloadValid() {
      return this.programAdminDetailsValid && this.programDetailsValid;
    },

    seats() {
      return Number(this._.get(this.programDetailsData, "seats", 1));
    },

    disableSubmitProgram() {
      return (
        this.success ||
        this.processing ||
        !this.createProgramPayloadValid ||
        !this.termsAccepted
      );
    }
  },

  methods: {
    ...mapActions("program", ["storeProgram"]),

    convertNewBrandingToProductName(productName) {
      switch (productName) {
        case "bewellcore":
          return "bewellcore";
        case "bewellplus":
          return "bewellplus";
        case "bewellpro":
          return "bewellpro";
        default:
          return "bewellcore";
      }
    },

    newProgramAdminDetails($event) {
      this.programAdminDetailsValid = $event.isValid;
      this.programAdminDetailsData = $event.formData;
    },

    newProgramDetails($event) {
      this.programDetailsValid = $event.isValid;
      this.programDetailsData = $event.formData;
    },

    setTermsAccepted(value) {
      this.termsAccepted = value;
    },

    async submitProgramSetup() {
      if (this.disableSubmitProgram) return false;
      this.processing = true;
      try {
        await this.createProgram();
        await this.login();
        this.success = true;
        this.submitError = false;
      } catch (error) {
        this.submitError = true;
      }
      this.processing = false;
    },

    async createProgram() {
      if (!this.createProgramPayloadValid) {
        throw Error("invalid program details");
      }

      const formData = new FormData();
      formData.set("name", this.createProgramPayload.programName);
      formData.set("url", this.createProgramPayload.programUrl);
      formData.set("timezone", `-${new Date().getTimezoneOffset() / 60}`);
      formData.set("email", this.createProgramPayload.email);
      formData.set("username", this.createProgramPayload.username);
      formData.set("firstName", this.createProgramPayload.firstName);
      formData.set("lastName", this.createProgramPayload.lastName);
      formData.set("password", this.createProgramPayload.password);
      formData.set(
        "password_confirmation",
        this.createProgramPayload.passwordConfirm
      );
      formData.set("type", this.activeProductName.toLowerCase());
      formData.set("seats", 0);
      formData.set("allowRegistration", 1);
      formData.set(
        "totalEligibleEmployees",
        this.createProgramPayload.totalEligibleEmployees
      );

      const payload = {
        formData
      };
      try {
        const response = await this.storeProgram(payload);
        this.programCreatedId = response.data.data.id;
        this.programCreated = true;
        this.programStoreError = false;
      } catch (error) {
        this.programStoreError = true;
        throw error;
      }
    },

    async login() {
      const params = {
        username: this.programAdminDetailsData.username,
        password: this.programAdminDetailsData.password,
        programId: this.programCreatedId
      };
      try {
        await this.$auth.login({
          data: params,
          redirect: false
        });
        const token = this.$auth.token();
        window.location.href = `https://${this.createProgramPayload.programUrl}.${process.env.VUE_APP_URL_DOMAIN}?npt=${token}`; // new_program_token = npt
      } catch (error) {
        throw error;
      }
    }
  },

  beforeRouteEnter(to, _, next) {
    next(async vm => {
      // Validate Product from URL
      let productParam = to.path.split("-")[1];
      vm.activeProductName = [
        "/create-bewellcore",
        "/create-bewellplus",
        "/create-bewellpro"
      ].includes(to.path)
        ? vm.convertNewBrandingToProductName(productParam)
        : "bewellcore";
    });
  }
};
</script>

<style lang="sass" scoped>
#setup-page
  min-height: 100%
  background-color: $white-ter
  .main-card
    +tablet
      max-width: 1200px
      width: auto
  .card-wrapper
    height: 100%
    padding: 1.2rem
  .create-program-column
    border: 1px solid $grey-light
    border-radius: $radius-large
</style>
